import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Loader2, Trash2, UserX, X } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import {
  Appointment,
  APPOINTMENT_TYPE_LABELS,
  AppointmentType,
  BLOCK_TYPE_LABELS,
  STATUS_COLORS,
} from '../types/appointment';
import { DeleteConfirmModal } from './DeleteConfirmModal';

interface Props {
  appointment: Appointment;
  onClose: () => void;
  onUpdated: () => void;
}

export function AppointmentDetailModal({ appointment, onClose, onUpdated }: Props) {
  const { patch, del, post } = useApi();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  // Reschedule state
  const [editing, setEditing] = useState(false);
  const [newDate, setNewDate] = useState(appointment.date);
  const [newTime, setNewTime] = useState(appointment.startTime);

  // Cancel state
  const [showCancel, setShowCancel] = useState(false);

  const isCancelled = appointment.status === 'cancelled';
  const isNoShow = appointment.status === 'no_show';

  const handleReschedule = useCallback(async () => {
    setError('');
    setProcessing(true);
    try {
      const result = await patch(`/appointments/${appointment.id}`, {
        date: newDate,
        startTime: newTime,
      });
      if (result.success) {
        onUpdated();
      } else {
        setError(result.error || 'Failed to reschedule');
      }
    } catch {
      setError('Failed to reschedule');
    } finally {
      setProcessing(false);
    }
  }, [appointment.id, newDate, newTime, patch, onUpdated]);

  const handleCancel = useCallback(async () => {
    setError('');
    setProcessing(true);
    try {
      const result = await del(`/appointments/${appointment.id}`, {
        data: { reason: 'Cancelled by staff' },
      });
      if (result.success) {
        onUpdated();
      } else {
        setError(result.error || 'Failed to cancel');
      }
    } catch {
      setError('Failed to cancel');
    } finally {
      setProcessing(false);
      setShowCancel(false);
    }
  }, [appointment.id, del, onUpdated]);

  const handleNoShow = useCallback(async () => {
    setError('');
    setProcessing(true);
    try {
      const result = await post(`/appointments/${appointment.id}/no-show`);
      if (result.success) {
        onUpdated();
      } else {
        setError(result.error || 'Failed to mark no-show');
      }
    } catch {
      setError('Failed to mark no-show');
    } finally {
      setProcessing(false);
    }
  }, [appointment.id, post, onUpdated]);

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900">
              {appointment.isBlockOut ? 'Block-Out' : 'Appointment'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
          </div>

          <div className="px-6 py-4 space-y-4">
            {error && <div className="p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

            {/* Status badge */}
            <div className={`inline-block px-2.5 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[appointment.status]}`}>
              {appointment.status.replace('_', ' ')}
            </div>

            {/* Details */}
            <dl className="space-y-3 text-sm">
              {!appointment.isBlockOut && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Patient</dt>
                  <dd>
                    <button
                      onClick={() => { onClose(); navigate(`/patients/${appointment.patientId}`); }}
                      className="font-medium text-blue-600 hover:text-blue-800"
                    >
                      {appointment.patientName || 'Unknown'}
                    </button>
                  </dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-gray-500">Date</dt>
                <dd className="font-medium text-gray-900">{appointment.date}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Time</dt>
                <dd className="font-medium text-gray-900">{appointment.startTime} – {appointment.endTime}</dd>
              </div>
              {!appointment.isBlockOut && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Type</dt>
                  <dd className="font-medium text-gray-900">
                    {APPOINTMENT_TYPE_LABELS[appointment.appointmentType as AppointmentType] || appointment.appointmentType}
                  </dd>
                </div>
              )}
              {appointment.isBlockOut && appointment.blockType && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Block Type</dt>
                  <dd className="font-medium text-gray-900">{BLOCK_TYPE_LABELS[appointment.blockType]}</dd>
                </div>
              )}
              {appointment.notes && (
                <div>
                  <dt className="text-gray-500 mb-1">Notes</dt>
                  <dd className="text-gray-900">{appointment.notes}</dd>
                </div>
              )}
              {appointment.cancellationReason && (
                <div>
                  <dt className="text-gray-500 mb-1">Cancellation Reason</dt>
                  <dd className="text-red-600">{appointment.cancellationReason}</dd>
                </div>
              )}
            </dl>

            {/* Reschedule form */}
            {editing && !isCancelled && (
              <div className="border-t border-gray-200 pt-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-900">Reschedule</h3>
                <div className="grid grid-cols-2 gap-3">
                  <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <input type="time" value={newTime} onChange={e => setNewTime(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="flex gap-2">
                  <button onClick={handleReschedule} disabled={processing}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
                    {processing ? 'Saving...' : 'Confirm Reschedule'}
                  </button>
                  <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Action buttons */}
            {!isCancelled && !editing && (
              <div className="flex items-center gap-2 border-t border-gray-200 pt-4">
                {!isNoShow && (
                  <button onClick={() => setEditing(true)}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1.5">
                    <Clock className="h-4 w-4" /> Reschedule
                  </button>
                )}
                {!appointment.isBlockOut && !isNoShow && (
                  <button onClick={handleNoShow} disabled={processing}
                    className="px-3 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 disabled:opacity-50 flex items-center gap-1.5">
                    <UserX className="h-4 w-4" /> No-Show
                  </button>
                )}
                <button onClick={() => setShowCancel(true)} disabled={processing}
                  className="px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50 flex items-center gap-1.5">
                  <Trash2 className="h-4 w-4" /> Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showCancel && (
        <DeleteConfirmModal
          title="Cancel Appointment"
          description={`Cancel the appointment for ${appointment.patientName || 'this block-out'} on ${appointment.date} at ${appointment.startTime}?`}
          onConfirm={handleCancel}
          onCancel={() => setShowCancel(false)}
        />
      )}
    </>
  );
}
